import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authService from '../../services/auth/authService';

// Estado inicial optimizado
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,       // verificación inicial al abrir la app
  isLoginLoading: false, // solo durante el proceso de login
  error: null
};

// Thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const response = await authService.login(username, password);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.non_field_errors?.[0] || 
        error.response?.data?.detail || 
        'Error al iniciar sesión'
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return true;
    } catch (error) {
      return rejectWithValue('Error al cerrar sesión');
    }
  }
);

export const checkAuthStatus = createAsyncThunk(
  'auth/checkStatus',
  async (_, { rejectWithValue, getState }) => {
    try {
      console.log('Verificando estado de autenticación...');

      // Verificación rápida de token local primero
      const token = await authService.getToken();
      if (!token) {
        console.log('No hay token almacenado');
        return { user: null, token: null };
      }

      // Si ya hay un usuario en el store (login reciente), solo refrescar datos
      const currentState = getState().auth;
      if (currentState.isAuthenticated && currentState.user) {
        console.log('Ya autenticado, refrescando datos del usuario...');
        try {
          const user = await authService.getCurrentUser();
          return { user: user || currentState.user, token };
        } catch (_) {
          // Si falla el refresh, mantener el usuario actual
          return { user: currentState.user, token };
        }
      }

      console.log('Token encontrado, verificando con servidor...');

      // Primera verificación: validar token con servidor
      const isAuth = await authService.isAuthenticated();

      if (isAuth) {
        console.log('Token válido, configurando interceptor...');
        await authService.setupTokenInterceptor();
        const user = await authService.getCurrentUser();
        console.log('Usuario autenticado:', user?.email);
        return { user, token };
      }

      console.log('Token inválido o expirado');
      return { user: null, token: null };
    } catch (error) {
      console.error('Error al verificar autenticación:', error);
      // Si hay error de red, mantener el estado actual si ya estaba autenticado
      const currentState = getState().auth;
      if (currentState.isAuthenticated && currentState.user) {
        return { user: currentState.user, token: currentState.token };
      }
      return { user: null, token: null };
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    forceLogout: (state) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login — usa isLoginLoading, NO isLoading (evita mostrar SplashScreen)
      .addCase(loginUser.pending, (state) => {
        state.isLoginLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoginLoading = false;
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;

        if (typeof window !== 'undefined' && window.clarity) {
          window.clarity('identify', action.payload.user.id.toString(), {
            email: action.payload.user.email,
            is_pilot: action.payload.user.is_pilot,
            is_enterprise_user: action.payload.user.is_enterprise_user,
            is_staff: action.payload.user.is_staff
          });
          window.clarity('event', 'user_login');
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoginLoading = false;
        state.error = action.payload;
      })
      
      // Logout — no usa isLoading para no mostrar SplashScreen
      .addCase(logoutUser.pending, (state) => {
        // No poner isLoading:true — el SplashScreen no debe aparecer al cerrar sesión
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        
        // Track logout en Clarity
        if (typeof window !== 'undefined' && window.clarity) {
          window.clarity('event', 'user_logout');
        }
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        // Asegurar que el usuario se desconecte incluso si hay un error
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      
      // Check Auth Status
      .addCase(checkAuthStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuthStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = !!action.payload.token;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(checkAuthStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        // Si hay un error, asegurar que el usuario no esté autenticado
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      });
  }
});

export const { clearError, forceLogout } = authSlice.actions;
export default authSlice.reducer;